// src/pages/analysis-result/index.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Icon from "../../components/AppIcon";
import { buildPdfReport } from "../../utils/pdfReport";

const isValidNumber = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  const n = Number(v);
  return Number.isFinite(n);
};

export default function AnalysisResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const initial = location.state || null;

  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // NewAnalysis'ten gelen state: { ...apiResponse, patientName, notes, analysisDate }
  const payload = useMemo(() => {
    if (!initial) return null;
    return initial.result || initial.data || initial;
  }, [initial]);

  const patientName = initial?.patientName || payload?.patientName || "";
  const notes = initial?.notes || payload?.notes || "";
  const analysisDate = initial?.analysisDate || payload?.analysisDate || "";

  // ✅ inference.py'den gelen ana sayı (adjusted_count)
  const graftCount =
    payload?.graft_count ??
    payload?.graftCount ??
    payload?.total_grafts ??
    payload?.count ??
    null;

  // Estimated range: raw * 1.10 → raw * 1.25
  const graftCountDisplay = useMemo(() => {
    if (!isValidNumber(graftCount)) return null;
    const base = Number(graftCount);
    if (base <= 0) return null;
    const minV = Math.floor(base * 1.10);
    const maxV = Math.ceil(base * 1.25);
    return `${minV}–${maxV}`;
  }, [graftCount]);

  const highDensityPercent =
    payload?.highDensityPercent ?? payload?.densityHigh ?? null;
  const lowDensityPercent =
    payload?.lowDensityPercent ?? payload?.densityLow ?? null;

  const pickUrl = (...keys) => {
    for (const k of keys) {
      const v = payload?.[k] ?? initial?.[k];
      if (typeof v === "string" && v.trim().length > 5) return v.trim();
    }
    return "";
  };

  const originalUrl = pickUrl(
    "original_url",
    "originalUrl",
    "input_url",
    "inputUrl"
  );
  const overlayUrl = pickUrl(
    "overlay_url",
    "overlayUrl",
    "marked_image_url",
    "markedImageUrl",
    "processed_image_url",
    "processedImageUrl"
  );
  const heatmapUrl = pickUrl(
    "heatmap_url",
    "heatmapUrl",
    "density_map_url",
    "densityMapUrl"
  );

  const hasData = !!payload;

  const breadcrumbItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "Analysis Result", path: "/analysis-result" },
  ];

  const handleDownloadReport = async () => {
    if (!hasData) return;
    if (!overlayUrl && !heatmapUrl) {
      alert("Report images are not available yet.");
      return;
    }

    setIsPdfLoading(true);
    try {
      const safeName = (patientName || "Patient")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^\w-]/g, "");

      // ✅ PDF'e de aynı aralık stringini gönder
      const graftText =
        graftCountDisplay ??
        (isValidNumber(graftCount) ? String(Math.round(Number(graftCount))) : "—");

      await buildPdfReport({
        patientName: patientName || "Unknown Patient",
        graftCount: graftText,
        highDensityPercent,
        lowDensityPercent,
        overlayUrl,
        heatmapUrl,
        filename: `${safeName}_Report.pdf`,
      });
    } catch (err) {
      console.error("PDF oluşturulamadı:", err);
      alert("PDF oluşturulamadı. Konsolu kontrol edin.");
    } finally {
      setIsPdfLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* BG (NewAnalysis estetiği) */}
      <div className="fixed inset-0 -z-10 bg-[url('/assets/images/medical.jpg')] bg-cover bg-center bg-no-repeat" />
      <div className="fixed inset-0 -z-10 bg-black/75" />

      <div className="relative z-10 min-h-screen overflow-y-auto pb-24">
        <Header />

        <main className="pt-10 px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Header Card */}
            <div className="mt-8 bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8 shadow-xl">
              <h1 className="text-3xl text-white font-light tracking-tight flex items-center gap-3">
                <Icon name="ScanSearch" size={28} color="white" />
                Image Analysis Result
              </h1>
              <p className="text-slate-200 mt-3">
                View graft estimation outputs and generate a PDF report.
              </p>

              {!hasData && (
                <div className="mt-6 bg-white/5 border border-white/10 rounded-2xl p-5">
                  <p className="text-slate-200">
                    Result data not found. This can happen if you refreshed the
                    page.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={() => navigate("/new-analysis")}
                      className="px-5 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition"
                    >
                      Start New Analysis
                    </button>
                    <Link
                      to="/dashboard"
                      className="px-5 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-200 text-sm hover:bg-white/10 transition"
                    >
                      Back to Dashboard
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {hasData && (
              <>
                {/* Patient Info Card */}
                <div className="mt-10 bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8 shadow-xl">
                  <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
                    <div>
                      <h2 className="text-white text-lg mb-2 flex items-center gap-2">
                        <Icon name="User" size={18} color="white" />
                        Patient
                      </h2>
                      <p className="text-slate-200">
                        <span className="text-white font-medium">
                          {patientName || "—"}
                        </span>
                        {analysisDate ? (
                          <span className="text-slate-300">
                            {" "}
                            • {analysisDate}
                          </span>
                        ) : null}
                      </p>

                      {notes ? (
                        <p className="text-slate-300 text-sm mt-3 whitespace-pre-wrap">
                          {notes}
                        </p>
                      ) : null}
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4 min-w-[220px]">
                      <p className="text-slate-300 text-xs">
                        Detected graft count
                      </p>
                      <p className="text-white text-2xl font-semibold mt-1">
                        {isValidNumber(graftCount) ? Math.round(Number(graftCount)) : "—"}
                      </p>
                      <p className="text-slate-300 text-xs mt-3">
                        Estimated range
                      </p>
                      <p className="text-white text-lg font-medium mt-1">
                        {graftCountDisplay ?? "—"}
                      </p>

                      {(highDensityPercent !== null ||
                        lowDensityPercent !== null) && (
                        <div className="mt-3 text-sm text-slate-200 space-y-1">
                          {highDensityPercent !== null && (
                            <div className="flex justify-between gap-4">
                              <span className="text-slate-300">High density</span>
                              <span className="text-white">
                                {highDensityPercent}%
                              </span>
                            </div>
                          )}
                          {lowDensityPercent !== null && (
                            <div className="flex justify-between gap-4">
                              <span className="text-slate-300">Low density</span>
                              <span className="text-white">
                                {lowDensityPercent}%
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Images + Actions */}
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
                  {/* Left: Images */}
                  <div className="lg:col-span-8 space-y-8">
                    {/* Overlay */}
                    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-6 shadow-xl">
                      <h3 className="text-white text-lg mb-4 flex items-center gap-2">
                        <Icon name="Image" size={18} color="white" />
                        Detected Grafts Overlay
                      </h3>

                      {overlayUrl ? (
                        <img
                          src={overlayUrl}
                          alt="Overlay"
                          crossOrigin="anonymous"
                          className="w-full rounded-2xl border border-white/10"
                          onError={() => console.error("Overlay load error")}
                        />
                      ) : (
                        <p className="text-slate-300 text-sm">
                          Overlay not available.
                        </p>
                      )}
                    </div>

                    {/* Heatmap */}
                    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-6 shadow-xl">
                      <h3 className="text-white text-lg mb-4 flex items-center gap-2">
                        <Icon name="Map" size={18} color="white" />
                        Density Map
                      </h3>

                      {heatmapUrl ? (
                        <img
                          src={heatmapUrl}
                          alt="Heatmap"
                          crossOrigin="anonymous"
                          className="w-full rounded-2xl border border-white/10"
                          onError={() => console.error("Heatmap load error")}
                        />
                      ) : (
                        <p className="text-slate-300 text-sm">
                          Heatmap not available.
                        </p>
                      )}
                    </div>

                    {/* Original (optional) */}
                    {originalUrl ? (
                      <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-6 shadow-xl">
                        <h3 className="text-white text-lg mb-4 flex items-center gap-2">
                          <Icon name="Camera" size={18} color="white" />
                          Original Image
                        </h3>
                        <img
                          src={originalUrl}
                          alt="Original"
                          crossOrigin="anonymous"
                          className="w-full rounded-2xl border border-white/10"
                        />
                      </div>
                    ) : null}
                  </div>

                  {/* Right: Actions */}
                  <div className="lg:col-span-4 space-y-6">
                    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-6 shadow-xl">
                      <h3 className="text-white text-lg mb-4 flex items-center gap-2">
                        <Icon name="FileDown" size={18} color="white" />
                        Actions
                      </h3>

                      <button
                        onClick={handleDownloadReport}
                        disabled={isPdfLoading}
                        className={`w-full py-3 rounded-xl border text-sm font-semibold transition shadow-lg
                          ${
                            isPdfLoading
                              ? "bg-white/5 border-white/10 text-slate-400 cursor-not-allowed"
                              : "bg-white text-black border-transparent hover:bg-slate-100"
                          }`}
                      >
                        {isPdfLoading ? "Generating PDF..." : "Download PDF Report"}
                      </button>

                      <div className="mt-3 grid grid-cols-1 gap-3">
                        <button
                          onClick={() => navigate("/new-analysis")}
                          className="w-full py-3 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition"
                        >
                          Start New Analysis
                        </button>

                        <button
                          onClick={() => navigate("/dashboard")}
                          className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-slate-200 text-sm hover:bg-white/10 transition"
                        >
                          Back to Dashboard
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}